/**
 * Curated text — what an officer or admin writes for the whole unit to read — parsed into the
 * small tree `ui/CuratedText.tsx` draws. TROOP-255, for an announcement's body; TROOP-256's Info
 * page is meant to reuse it rather than grow a second one.
 *
 * **The line is drawn by who writes the text, not by the feature.** Curated text may carry working
 * links. A message, a reply, anything posted in a discussion, never comes through here and stays
 * literal characters — `CuratedTextSurfaceTests` holds that from the repository's side.
 *
 * The grammar, and nothing else: paragraphs (a blank line between), line breaks kept, `-`/`*` and
 * `1.` lists, `**bold**`, `*italic*`, and inline links `[text](https://…)`. **No images, no raw
 * HTML, no autolinking** of a bare address, no headings. The tree holds strings and React draws
 * them as text, so there is no HTML anywhere to sanitise.
 *
 * **The server checked every link on save** (`CuratedText` in the domain, which spells
 * `INLINE_LINK` the same way), and this checks again on its own account: a target that is not a
 * plain https address is drawn as the characters that were typed. A link is never drawn without
 * its hostname beside it — §7's mitigation, and the reason links are allowed here at all.
 *
 * Self-contained on purpose: `npm test` runs this under Node's own runner, which resolves imports
 * literally, so it reaches for nothing else in `src/`.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  /** `host` is the punycode hostname the browser will actually go to. */
  | { kind: 'link'; label: string; href: string; host: string }
  | { kind: 'break' };

export type Block =
  | { kind: 'paragraph'; content: Inline[] }
  | { kind: 'list'; ordered: boolean; start: number; items: Inline[][] };

/**
 * An inline link. The domain's `CuratedText.InlineLink` is this pattern, character for character:
 * a label with no brackets or line break, a target with no whitespace, parentheses or angle
 * brackets.
 */
export const INLINE_LINK = /\[([^[\]\r\n]+)\]\(([^\s()<>]+)\)/g;

/** `**bold**` or `*italic*`, with no space just inside a marker and no line break. */
const EMPHASIS = /\*\*(?=\S)([^*\r\n]+?)(?<=\S)\*\*|\*(?=\S)([^*\r\n]+?)(?<=\S)\*/g;

/** A list item: up to three spaces, a `-`, `*` or `1.`/`1)` marker, a space, the item. */
const LIST_ITEM = /^ {0,3}(?:([-*])|(\d{1,9})[.)])[ \t]+(.*)$/;

/**
 * Where a link may go, and the host to print beside it — or null, and the link is drawn as the
 * characters that were typed.
 *
 * https only; no user name or password before the host (`https://council.org@elsewhere/` goes to
 * *elsewhere*). The `href` is the parser's own serialisation and the `host` is read from that same
 * parse, so the host printed is the host the browser is sent to — and an international name comes
 * back as its `xn--` form, so a look-alike spelled in another script reads as what it is.
 */
export function safeLink(target: string): { href: string; host: string } | null {
  let url: URL;
  try {
    url = new URL(target);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:' || url.username || url.password || !url.hostname) {
    return null;
  }

  return { href: url.href, host: url.hostname };
}

/** The text as blocks, in order. Empty or blank text is no blocks. */
export function parseCuratedText(text: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] | null = null;
  let list: { ordered: boolean; start: number; items: string[] } | null = null;

  const close = () => {
    if (paragraph) {
      blocks.push({ kind: 'paragraph', content: parseLines(paragraph) });
    }
    if (list) {
      blocks.push({
        kind: 'list',
        ordered: list.ordered,
        start: list.start,
        items: list.items.map((item) => parseInline(item)),
      });
    }
    paragraph = null;
    list = null;
  };

  for (const line of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (line.trim() === '') {
      close();
      continue;
    }

    const item = LIST_ITEM.exec(line);

    if (item) {
      const ordered = item[2] !== undefined;

      if (!list || list.ordered !== ordered) {
        close();
        list = { ordered, start: ordered ? Number(item[2]) : 1, items: [] };
      }

      list.items.push(item[3]);
      continue;
    }

    // A line that is not an item ends a list rather than continuing its last item: a leader
    // typing a list and then a sentence means a list and then a sentence.
    if (!paragraph) {
      close();
      paragraph = [];
    }

    paragraph.push(line);
  }

  close();
  return blocks;
}

/** A paragraph's lines, with the breaks between them kept: three typed lines mean three lines. */
function parseLines(lines: string[]): Inline[] {
  return lines.flatMap((line, i) =>
    i === 0 ? parseInline(line) : [{ kind: 'break' } as Inline, ...parseInline(line)],
  );
}

/**
 * Stands in for a drawn link while emphasis is found around it. A private-use character, so a
 * typed one is replaced before parsing rather than mistaken for a slot.
 */
const SLOT = String.fromCharCode(0xe000);

/**
 * One line's inline content.
 *
 * **Links first, on the raw line** — the string the server checked — and emphasis afterwards
 * around them. The other order let an asterisk split a link the server had accepted:
 * `[**Medical form**](https://…)` drew as brackets and an address with nothing to tap, which is
 * exactly the "written as a link, not drawn as one" the server's refusal exists to prevent. So
 * `**[form](https://…)**` is a bold link, emphasis inside a label is dropped rather than drawn, and
 * an asterisk in an address is part of the address.
 */
export function parseInline(text: string): Inline[] {
  const links: Inline[] = [];
  const line = text.replaceAll(SLOT, String.fromCharCode(0xfffd));

  const masked = line.replace(INLINE_LINK, (whole: string, label: string, target: string, at: number) => {
    const safe = safeLink(target);

    // An image is not drawn -- `![alt](...)` stays the characters typed (and the server refuses
    // it on save) -- and neither is a target the check above refuses.
    if (!safe || line[at - 1] === '!') {
      return whole;
    }

    links.push({ kind: 'link', label: plainLabel(label), href: safe.href, host: safe.host });
    return SLOT;
  });

  let next = 0;
  const unmask = (piece: string): Inline[] =>
    piece.split(SLOT).flatMap((run, i) => {
      const parts: Inline[] = i > 0 ? [links[next++]] : [];
      return run ? [...parts, { kind: 'text', text: run } as Inline] : parts;
    });

  const out: Inline[] = [];
  let last = 0;

  for (const match of masked.matchAll(EMPHASIS)) {
    const at = match.index ?? 0;
    if (at > last) {
      out.push(...unmask(masked.slice(last, at)));
    }

    const strong = match[1] !== undefined;
    out.push({ kind: strong ? 'strong' : 'em', children: unmask(strong ? match[1] : match[2]) });
    last = at + match[0].length;
  }

  if (last < masked.length) {
    out.push(...unmask(masked.slice(last)));
  }

  return merge(out);
}

/** A label's text with any emphasis markers taken off — the link itself is what stands out. */
function plainLabel(label: string): string {
  return label.replace(EMPHASIS, (_whole: string, strong?: string, em?: string) => strong ?? em ?? '');
}

/** Adjacent text runs as one, so the tree a test reads is the tree a person would write. */
function merge(inlines: Inline[]): Inline[] {
  const out: Inline[] = [];

  for (const inline of inlines) {
    const previous = out[out.length - 1];

    if (inline.kind === 'text' && previous?.kind === 'text') {
      out[out.length - 1] = { kind: 'text', text: previous.text + inline.text };
    } else {
      out.push(inline);
    }
  }

  return out;
}
